import React, { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import moment from 'moment-timezone';

const PlaceAutocomplete = ({
  onPlaceSelected,
  defaultValue,
  onChange,
  placeholder,
  style,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(defaultValue || '');
  const [value, setValue] = useState(defaultValue || null);

  useEffect(() => {
    if (defaultValue && !inputValue) {
      setInputValue(defaultValue);
      setValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    let active = true;

    if (!inputValue || inputValue.length < 3) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const timer = setTimeout(() => {
      let countryFilter = "";
      try {
        const merchantData = localStorage.getItem("merchantData") ? JSON.parse(localStorage.getItem("merchantData")) : null;
        const locale = merchantData?.locale || localStorage.getItem("locale") || "en-US";
        const parts = locale.split(/[-_]/);
        if (parts.length > 1) {
          countryFilter = `&countrycodes=${parts[1].toLowerCase()}`;
        } else {
          // Fallback to timezone if locale is just a language code
          const tz = merchantData?.timezone || moment.tz.guess();
          const zone = moment.tz.zone(tz);
          if (zone && zone.countries && zone.countries().length > 0) {
            countryFilter = `&countrycodes=${zone.countries()[0].toLowerCase()}`;
          }
        }
      } catch (e) {
        console.warn("Could not determine country code", e);
      }

      fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(inputValue)}&format=json&addressdetails=1&limit=5${countryFilter}`, {
        headers: {
          'Accept-Language': 'en'
        }
      })
        .then((res) => res.json())
        .then((data) => {
          if (active) {
            setOptions(data || []);
            setLoading(false);
          }
        })
        .catch((err) => {
          console.error("Address search error:", err);
          if (active) setLoading(false);
        });
    }, 600);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [inputValue]);

  return (
    <div style={{ width: '100%', ...style }}>
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        isOptionEqualToValue={(option, val) => 
          typeof option === 'string' ? option === val : option.display_name === (val?.display_name || val)
        }
        getOptionLabel={(option) => typeof option === 'string' ? option : (option.display_name || '')}
        options={options}
        loading={loading}
        freeSolo
        value={value}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          if (onChange) {
            onChange({ target: { value: newInputValue } });
          }
        }}
        onChange={(event, newValue) => {
          setValue(newValue);
          if (newValue && typeof newValue !== 'string') {
            if (onPlaceSelected) {
              onPlaceSelected({
                formatted_address: newValue.display_name,
                name: newValue.display_name,
                geometry: {
                  location: {
                    lat: () => parseFloat(newValue.lat),
                    lng: () => parseFloat(newValue.lon)
                  }
                }
              });
            }
            if (onChange) {
              onChange({ target: { value: newValue.display_name } });
            }
          } else if (typeof newValue === 'string') {
             if (onChange) {
                onChange({ target: { value: newValue } });
             }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || "Search for an address"}
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    </div>
  );
};

export default PlaceAutocomplete;
